import { inject } from "vue";

export function useGoogleMapsAutocomplete() {
    const emitter = inject<any>("mitt");
    let autocomplete: any = null;

    function autocompletedAddress() {
        const place = autocomplete.getPlace();

        if (!place?.address_components) {
            return {};
        }

        const address: Record<string, any> = {
            line1: "",
            postal_code: "",
        };

        for (const component of place.address_components) {
            const componentTypes = component.types;

            switch (true) {
                case componentTypes.includes("subpremise"): {
                    address.line1 = `${component.long_name}/`;
                    break;
                }

                case componentTypes.includes("street_number"): {
                    address.line1 = `${address.line1}${component.long_name}`;
                    break;
                }

                case componentTypes.includes("route"): {
                    address.line1 = `${address.line1} ${component.long_name}`;
                    break;
                }

                case componentTypes.includes("postal_code"): {
                    address.postal_code = `${component.long_name}${address.postal_code}`;
                    break;
                }

                case componentTypes.includes("postal_code_suffix"): {
                    address.postal_code = `${address.postal_code}-${component.long_name}`;
                    break;
                }

                case componentTypes.includes("sublocality"):
                case componentTypes.includes("sublocality_level_1"):
                    address.line2 = component.long_name;
                    break;

                case componentTypes.includes("postal_town"):
                case componentTypes.includes("locality"):
                    address.city = component.long_name;
                    break;

                case componentTypes.includes("administrative_area_level_1"): {
                    address.state = component.short_name;
                    break;
                }

                case componentTypes.includes("country"):
                    address.country_code = component.short_name;
                    break;
            }
        }

        if (address.country_code === "US") {
            address.line2 = null;
        }

        return address;
    }

    function destroyAutocomplete() {
        if (googleMapsLoaded()) {
            google.maps.event.clearInstanceListeners(autocomplete);
        }
    }

    function googleMapsLoaded() {
        return (
            typeof google !== "undefined" &&
            typeof google.maps !== "undefined" &&
            typeof google.maps.places !== "undefined"
        );
    }

    function initializeAutocomplete(
        field: any,
        countryCode: string,
        onPlaceChanged: () => void,
    ) {
        registerInput(field, countryCode, onPlaceChanged);

        emitter.on("googleMapsLoaded", () =>
            registerInput(field, countryCode, onPlaceChanged),
        );
    }

    function onEnterKeyDown(e: KeyboardEvent) {
        const pacContainer = document.getElementsByClassName("pac-container");

        for (const i in pacContainer) {
            if (
                pacContainer.length > 0 &&
                (pacContainer[i] as HTMLElement).offsetWidth > 0
            ) {
                e.preventDefault();
            }
        }
    }

    function pasteAutocompletedAddress(inputs: any) {
        const address = autocompletedAddress();

        for (const field in address) {
            if (
                Object.prototype.hasOwnProperty.call(inputs, field) &&
                Object.prototype.hasOwnProperty.call(address, field)
            ) {
                inputs[field] = address[field];
            }
        }
    }

    function registerInput(
        field: any,
        countryCode: string,
        onPlaceChanged: () => void,
    ) {
        if (googleMapsLoaded()) {
            autocomplete = new google.maps.places.Autocomplete(field, {
                componentRestrictions: { country: [countryCode] },
                fields: ["address_components", "geometry"],
                types: ["address"],
            });

            autocomplete.addListener("place_changed", onPlaceChanged);
        }
    }

    function restrictAutocompleteByCountry(countryCode: string) {
        if (autocomplete) {
            autocomplete.setComponentRestrictions({
                country: countryCode,
            });
        }
    }

    return {
        destroyAutocomplete,
        initializeAutocomplete,
        onEnterKeyDown,
        pasteAutocompletedAddress,
        restrictAutocompleteByCountry,
    };
}
